import { ChangelogPlatform } from '../../data/changelog';

export interface SystemStatus {
  service_name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  message: string;
  updated_at: string;
}

export interface MaintenanceWindow {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  affected_services: string[];
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
}

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  category: 'feature' | 'improvement' | 'bugfix' | 'security' | 'breaking';
  published_at: string;
}

// Derive platform from the version string — the changelog table has no
// platform column (no schema change was authorized). iOS builds use
// "Build NN"; Android checkpoints use "Android X.Y.Z". This keeps iOS
// and Android identity explicit and filterable without a schema change.
export function derivePlatform(version: string): ChangelogPlatform {
  if (version.startsWith('Android')) return 'android';
  return 'ios';
}

// Digit-wise version comparison: "Build 104" → [104],
// "Android 0.2.0-beta13" → [0, 2, 0, 13]. Versions within one platform
// share a shape, so comparing number groups in order picks the newest.
export function compareVersions(a: string, b: string): number {
  const numsA = (a.match(/\d+/g) ?? []).map(Number);
  const numsB = (b.match(/\d+/g) ?? []).map(Number);
  const len = Math.max(numsA.length, numsB.length);
  for (let i = 0; i < len; i++) {
    const diff = (numsA[i] ?? 0) - (numsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// Newest published version for one platform, from the full changelog —
// never scoped to the selected Release History tab or filters.
export function latestVersionForPlatform(
  entries: ChangelogEntry[],
  platform: ChangelogPlatform
): string | null {
  const versions = entries
    .filter(entry => derivePlatform(entry.version) === platform)
    .map(entry => entry.version);
  if (versions.length === 0) return null;
  return versions.reduce((a, b) => (compareVersions(a, b) >= 0 ? a : b));
}

// The Android summary stat historically shipped as a plain `value` in
// site_content (e.g. "0.2.0-beta5") and went stale at each checkpoint.
// Any Android-labeled or Android-version-shaped stat derives from the
// published changelog instead of trusting stored content.
export function isAndroidSummaryStat(stat: any): boolean {
  return (
    stat?.value_type === 'auto_android' ||
    /android/i.test(stat?.label ?? '') ||
    (typeof stat?.value === 'string' && /^\d+\.\d+\.\d+-beta\d+$/.test(stat.value.trim()))
  );
}

// Static fallback for the Beta Testing Guide content. Mirrors the
// canonical content in public.site_content (key = 'beta_status').
// Used when the get_site_content RPC fails or returns empty.
export const DEFAULT_BETA_STATUS = {
  visible: true,
  title: 'Beta Testing Guide',
  subtitle: 'Quick tips for testing and sharing feedback',
  tips_header: 'Testing Tips',
  reporting_header: 'Share Feedback',
  tips: [
    {
      title: 'Keep RidePulse updated.',
      description:
        'Install the latest build before testing. New fixes and features land in every release.',
    },
    {
      title: 'Try again once if something looks wrong.',
      description:
        'A quick retry rules out a one-time glitch. If it happens again, it is worth reporting.',
    },
    {
      title: 'Send feedback if it happens again.',
      description:
        'A short note about what you saw and what you expected is all we need.',
    },
    {
      title: 'A short note is enough.',
      description:
        'Screenshots are optional. You do not need to send logs, build numbers, or device details.',
    },
    {
      title: 'Test core flows each build.',
      description:
        'Create an event, join a ride, send a chat, edit your profile, and open the map. These are the flows that break most often.',
    },
    {
      title: 'Try real conditions.',
      description:
        'Test with poor signal, low battery, or while switching between apps. Motorcycle apps live on the road, not on Wi-Fi.',
    },
  ],
  reporting: [
    {
      icon: '📱',
      title: 'In-app feedback (when available)',
      description:
        'RidePulse can include useful app details automatically when you send feedback from inside the app.',
      items: [
        'Open Settings and tap Contact Support',
        'Your email opens with app version and build info filled in',
        'Add a short note about what you saw',
        'Send to support@ridepulse.app',
        'On iOS with TestFlight, you can also take a screenshot while the app is open. TestFlight may prompt you to send it as feedback.',
        'On Android, use the in-app Contact Support button. Android does not have a built-in screenshot feedback option.',
      ],
    },
    {
      icon: '💬',
      title: 'Web feedback form',
      description:
        'Prefer the web? Use our feedback form. Choose a topic and add a short note.',
      link_url: '/contact',
      link_text: 'Open feedback form',
    },
  ],
  stats: [
    { label: 'Latest iOS Build', value_type: 'auto_build' },
    { label: 'Latest Android Beta', value_type: 'auto_android' },
    { label: 'Platforms in Testing', value: 'iOS + Android' },
    { label: 'Test Channels', value: 'TestFlight +\nGoogle Play Internal Testing' },
  ],
};

const INVALID_DATE = 'Update time unavailable';

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return INVALID_DATE;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return INVALID_DATE;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};
